import { ArrowRight, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import type {
  CampaignAnalysisInsight,
  CampaignAnalysisKeyInsightCard,
  CampaignAnalysisRepresentativePostExample,
} from '@/entities/campaign/generated/campaign-type';
import {
  BASELINE_INSIGHT_RATIO,
  formatCompactNumber,
  formatPercent,
  getSummaryCardHeading,
  MAX_INSIGHT_RATIO,
  MAX_REPORT_INSIGHTS_COUNT,
} from '../../helpers/analysis-report';
import { Badge } from '@/shared/components/shadcn/badge';
import { Card, CardContent } from '@/shared/components/shadcn/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/shadcn/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';
import { cn } from '@/shared/utils';

type InsightSummaryCard = CampaignAnalysisKeyInsightCard;

export function InsightsSection({
  insightCards,
  insights,
}: {
  insightCards: readonly InsightSummaryCard[];
  insights: readonly CampaignAnalysisInsight[];
}) {
  const visibleInsights = insights.slice(0, MAX_REPORT_INSIGHTS_COUNT);

  return (
    <div className="flex flex-col gap-4">
      {visibleInsights.map((insight, index) => (
        <InsightItem
          key={insight.title}
          category={insightCards.at(index)?.heading}
          defaultOpen={index === 0}
          insight={insight}
        />
      ))}
    </div>
  );
}

function InsightItem({
  category,
  defaultOpen,
  insight,
}: {
  category?: string;
  defaultOpen: boolean;
  insight: CampaignAnalysisInsight;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const insightTitle = getInsightDisplayTitle(insight.title);
  const categoryLabel = category ? getSummaryCardHeading(category) : undefined;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card
        data-state={open ? 'open' : 'closed'}
        className="border-border bg-muted gap-0 overflow-hidden py-0 shadow-sm transition-[border-color,box-shadow] print:shadow-none"
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            data-campaign-analysis-print-insight-trigger
            className="group hover:bg-muted/80 focus-visible:ring-ring/50 flex w-full items-center justify-between gap-10 p-5 text-left transition-colors outline-none focus-visible:ring-[3px] sm:p-6 print:p-4"
            aria-label={`${open ? 'Collapse' : 'Expand'} insight: ${insightTitle}`}
          >
            <span className="flex min-w-0 flex-1 flex-col gap-4">
              <span className="flex min-w-0 flex-col gap-2">
                <span className="flex min-w-0 items-center gap-3">
                  {categoryLabel ? (
                    <span className="border-muted-foreground/40 text-muted-foreground inline-flex h-7 shrink-0 items-center justify-center rounded-full border px-3 text-xs leading-none font-medium">
                      {categoryLabel}
                    </span>
                  ) : null}
                  <span className="text-card-foreground min-w-0 text-lg leading-7 font-bold tracking-normal">
                    {insightTitle}
                  </span>
                </span>
                <span className="text-muted-foreground text-sm leading-5">
                  {renderTextWithBoldMultipliers(insight.lead)}
                </span>
              </span>
              <InsightActionGuide guide={insight.guide} />
            </span>
            <span
              data-campaign-analysis-print-insight-toggle
              className="text-foreground flex size-5 shrink-0 items-center justify-center transition-colors"
            >
              <ChevronDown
                aria-hidden="true"
                className={cn('size-5 shrink-0 transition-transform', open && 'rotate-180')}
              />
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent
          data-campaign-analysis-screen-insight-details
          forceMount
          className={cn(!open && 'hidden')}
        >
          <InsightDetailsContent insight={insight} />
        </CollapsibleContent>
        <div data-campaign-analysis-print-insight-details className="hidden">
          <InsightDetailsContent insight={insight} />
        </div>
      </Card>
    </Collapsible>
  );
}

function getInsightDisplayTitle(title: string) {
  return title.replace(/^\s*(?:do|don'?t)\s*[.:·-]\s*/i, '');
}

function renderTextWithBoldMultipliers(text: string) {
  const multiplierPattern = /(\d+(?:\.\d+)?\s*(?:x|X|배))/g;
  const exactMultiplierPattern = /^\d+(?:\.\d+)?\s*(?:x|X|배)$/;
  const segments = text.split(multiplierPattern);

  return segments.map((segment, index) =>
    exactMultiplierPattern.test(segment) ? (
      <strong key={`${segment}-${index}`} className="text-foreground font-semibold">
        {segment}
      </strong>
    ) : (
      segment
    ),
  );
}

function InsightDetailsContent({ insight }: { insight: CampaignAnalysisInsight }) {
  return (
    <CardContent className="border-border border-t p-0 print:border-0">
      <div className="bg-background p-0">
        <InsightComparisonTable insight={insight} />
      </div>
    </CardContent>
  );
}

function InsightActionGuide({ guide }: { guide: string }) {
  return (
    <div className="grid w-full grid-cols-[28px_minmax(0,1fr)] items-center gap-3">
      <span
        aria-hidden="true"
        className="border-border bg-background text-muted-foreground flex size-7 shrink-0 items-center justify-center rounded-full border"
      >
        <ArrowRight className="size-3.5" />
      </span>
      <p className="text-foreground text-sm leading-6 font-normal">{guide}</p>
    </div>
  );
}

type InsightComparison = CampaignAnalysisInsight['groups_compared'][number];

function renderInsightComparisonExample(comparison: InsightComparison) {
  const representativePost = comparison.representative_post_examples.at(0);

  return representativePost ? (
    <RepresentativePostCard post={representativePost} />
  ) : (
    <p className="text-muted-foreground text-sm leading-5">
      No representative post is available for this group.
    </p>
  );
}

function InsightComparisonTable({ insight }: { insight: CampaignAnalysisInsight }) {
  const sortedComparisons = [...insight.groups_compared].sort((first, second) => {
    const firstSupports =
      first.median_target_performance_metric_ratio_to_population >= BASELINE_INSIGHT_RATIO;
    const secondSupports =
      second.median_target_performance_metric_ratio_to_population >= BASELINE_INSIGHT_RATIO;

    return Number(secondSupports) - Number(firstSupports);
  });

  return (
    <>
      <div data-campaign-analysis-insight-mobile-list className="flex flex-col divide-y lg:hidden">
        {sortedComparisons.map((comparison) => (
          <InsightComparisonMobileRow
            key={comparison.name}
            comparison={comparison}
            renderExample={renderInsightComparisonExample}
          />
        ))}
      </div>
      <div data-campaign-analysis-insight-table className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow
              data-campaign-analysis-insight-table-row
              className="bg-background hover:bg-background gap-x-6 print:gap-x-4"
              gridTemplateColumns="minmax(320px,1.35fr) minmax(180px,0.75fr) 88px minmax(240px,0.85fr)"
            >
              <TableHead className="h-12 px-6 text-sm print:h-9 print:px-3 print:text-xs">
                Pattern
              </TableHead>
              <TableHead className="h-12 px-4 text-sm print:h-9 print:px-3 print:text-xs">
                Performance
              </TableHead>
              <TableHead className="h-12 px-4 text-sm print:h-9 print:px-3 print:text-xs">
                Posts
              </TableHead>
              <TableHead className="h-12 px-6 text-sm print:h-9 print:px-3 print:text-xs">
                Example
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedComparisons.map((comparison) => (
              <InsightComparisonDesktopRow
                key={comparison.name}
                comparison={comparison}
                renderExample={renderInsightComparisonExample}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function getInsightComparisonEvidence(comparison: InsightComparison) {
  const supports =
    comparison.median_target_performance_metric_ratio_to_population >= BASELINE_INSIGHT_RATIO;

  return {
    label: supports ? 'Won' : 'Lost',
    badgeClassName: supports
      ? 'border-chart-1/20 bg-chart-1/10 text-chart-1'
      : 'border-destructive/20 bg-destructive/10 text-destructive',
    barClassName: supports ? 'bg-chart-1' : 'bg-destructive/70',
  };
}

function InsightComparisonEvidenceBadge({ comparison }: { comparison: InsightComparison }) {
  const evidence = getInsightComparisonEvidence(comparison);

  return (
    <Badge
      variant="outline"
      className={cn(
        'mt-0.5 h-5 shrink-0 rounded-full px-2 py-0 text-xs leading-4 font-semibold',
        evidence.badgeClassName,
      )}
    >
      {evidence.label}
    </Badge>
  );
}

function InsightPerformanceSummary({ comparison }: { comparison: InsightComparison }) {
  const evidence = getInsightComparisonEvidence(comparison);

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="flex items-baseline gap-1.5">
        <span className="text-foreground text-sm leading-5 font-semibold">
          {comparison.median_target_performance_metric_ratio_to_population.toFixed(2)}x
        </span>
        <span className="text-muted-foreground text-xs leading-4 font-medium">vs all posts</span>
      </p>
      <InsightPerformanceBar
        ratio={comparison.median_target_performance_metric_ratio_to_population}
        barClassName={evidence.barClassName}
      />
    </div>
  );
}

function InsightComparisonDesktopRow({
  comparison,
  renderExample,
}: {
  comparison: InsightComparison;
  renderExample: (comparison: InsightComparison) => ReactNode;
}) {
  return (
    <TableRow
      data-campaign-analysis-insight-table-row
      data-campaign-analysis-print-avoid-break
      className="items-start gap-x-6 hover:bg-transparent print:gap-x-4"
      gridTemplateColumns="minmax(320px,1.35fr) minmax(180px,0.75fr) 88px minmax(240px,0.85fr)"
    >
      <TableCell className="flex-col items-start gap-2 px-6 py-5 whitespace-normal print:px-3 print:py-3">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-start gap-2">
            <InsightComparisonEvidenceBadge comparison={comparison} />
            <p className="text-foreground min-w-0 text-sm font-semibold">{comparison.name}</p>
          </div>
          <p className="text-muted-foreground text-sm leading-6">
            {renderTextWithBoldMultipliers(comparison.description)}
          </p>
        </div>
      </TableCell>
      <TableCell className="flex-col items-start gap-2 px-4 py-5 whitespace-normal print:px-3 print:py-3">
        <InsightPerformanceSummary comparison={comparison} />
      </TableCell>
      <TableCell className="flex-col items-start gap-1 px-4 py-5 whitespace-normal print:px-3 print:py-3">
        <p className="text-foreground text-sm font-semibold">{comparison.num_posts} posts</p>
      </TableCell>
      <TableCell className="items-start px-6 py-5 whitespace-normal print:px-3 print:py-3">
        <p className="sr-only">Example</p>
        {renderExample(comparison)}
      </TableCell>
    </TableRow>
  );
}

function InsightComparisonMobileRow({
  comparison,
  renderExample,
}: {
  comparison: InsightComparison;
  renderExample: (comparison: InsightComparison) => ReactNode;
}) {
  return (
    <div
      data-campaign-analysis-print-avoid-break
      className="flex flex-col gap-5 p-5 sm:p-6 print:gap-3 print:p-4"
    >
      <div className="flex flex-col items-start gap-2">
        <p className="text-muted-foreground text-sm font-medium">Pattern</p>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-start gap-2">
            <InsightComparisonEvidenceBadge comparison={comparison} />
            <p className="text-foreground min-w-0 text-sm font-semibold">{comparison.name}</p>
          </div>
          <p className="text-muted-foreground text-sm leading-6">
            {renderTextWithBoldMultipliers(comparison.description)}
          </p>
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-[minmax(0,0.6fr)_minmax(0,1fr)] print:grid-cols-[minmax(0,0.45fr)_minmax(0,1fr)] print:gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-muted-foreground text-sm font-medium">Posts</p>
          <p className="text-foreground text-sm font-semibold">{comparison.num_posts} posts</p>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground text-sm font-medium">Performance</p>
          <InsightPerformanceSummary comparison={comparison} />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-sm font-medium">Example</p>
        <div>{renderExample(comparison)}</div>
      </div>
    </div>
  );
}

function InsightPerformanceBar({ barClassName, ratio }: { barClassName: string; ratio: number }) {
  const baselinePosition = (BASELINE_INSIGHT_RATIO / MAX_INSIGHT_RATIO) * 100;
  const boundedRatio = Math.max(0, Math.min(ratio, MAX_INSIGHT_RATIO));
  const ratioPosition = (boundedRatio / MAX_INSIGHT_RATIO) * 100;
  const fillLeft = Math.min(ratioPosition, baselinePosition);
  const fillWidth = Math.abs(ratioPosition - baselinePosition);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="bg-muted relative h-2 overflow-hidden rounded-full">
        <div
          aria-hidden="true"
          className={cn('absolute top-0 h-full rounded-full', barClassName)}
          style={{
            left: `${fillLeft}%`,
            width: `${fillWidth}%`,
          }}
        />
        <div
          aria-hidden="true"
          className="bg-foreground/50 absolute top-1/2 h-4 w-px -translate-y-1/2"
          style={{ left: `${baselinePosition}%` }}
        />
      </div>
    </div>
  );
}

function RepresentativePostCard({ post }: { post: CampaignAnalysisRepresentativePostExample }) {
  const metricLines = getRepresentativePostMetricLines(post);

  return (
    <div className="grid gap-3 sm:grid-cols-[76px_minmax(0,1fr)] print:grid-cols-[60px_minmax(0,1fr)] print:gap-2">
      <a
        href={post.post_source_url}
        target="_blank"
        rel="noreferrer"
        className="border-border bg-background relative block aspect-[9/12] overflow-hidden rounded-md border print:w-15"
      >
        <img
          src={post.thumbnail_url}
          alt={`Post by @${post.creator_username}`}
          className="size-full object-cover"
          loading="lazy"
        />
      </a>
      <div className="min-w-0">
        <p className="text-foreground min-w-0 text-sm font-semibold break-words">
          @{post.creator_username}
        </p>
        <div className="text-muted-foreground mt-2 flex flex-col gap-0.5 text-sm leading-5">
          <p>{metricLines.views}</p>
          {metricLines.engagementRate ? <p>{metricLines.engagementRate}</p> : null}
        </div>
      </div>
    </div>
  );
}

function getRepresentativePostMetricLines(post: CampaignAnalysisRepresentativePostExample) {
  return {
    views: `${formatCompactNumber(post.views)} views`,
    engagementRate:
      post.engagement_rate === null ? undefined : `${formatPercent(post.engagement_rate)} ER`,
  };
}
